<template>
  <div
    id="app-sidebar-freelancer"
    class="bg-white hidden lg:block flex-shrink-0 overflow-y-auto shadow rounded-xl transition-all duration-300 pr-5 w-[280px]"
  >
    <div class="flex flex-col h-full">
      <div class="overflow-y-auto flex-1 min-h-0">
        <!-- Logo -->
        <div class="p-2 flex justify-center">
          <Image
            :src="Logo"
            alt="Symphony"
            width="150"
            class="p-2"
          />
        </div>

        <!-- Nav section -->
        <div class="px-2 pb-2">
          <div class="flex flex-col gap-1">
            <router-link
              to="/dashboard/freelance"
              class="flex items-center gap-2 p-2 text-surface-700 hover:bg-surface-100 rounded-lg transition-colors border-r-2 border-transparent"
              :class="{ '!border-amber-500 bg-amber-50 text-amber-700': route.path === '/dashboard/freelance' }"
            >
              <Image
                :src="DashboardIcon"
                alt="Dashboard"
                class="flex-shrink-0"
                width="15"
              />
              <span class="font-medium">Dashboard</span>
            </router-link>
            <router-link
              to="/freelance/contacts"
              class="flex items-center gap-2 p-2 text-surface-700 hover:bg-surface-100 rounded-lg transition-colors border-r-2 border-transparent"
              :class="{ '!border-amber-500 bg-amber-50 text-amber-700': route.path === '/freelance/contacts' }"
            >
              <Image
                :src="ContactsIcon"
                alt="Contacts"
                class="flex-shrink-0"
                width="15"
              />
              <span class="font-medium">Contacts</span>
            </router-link>
            <router-link
              to="/freelance/projects"
              class="flex items-center gap-2 p-2 text-surface-700 hover:bg-surface-100 rounded-lg transition-colors border-r-2 border-transparent"
              :class="{ '!border-amber-500 bg-amber-50 text-amber-700': route.path === '/freelance/projects' }"
            >
              <Image
                :src="ProjectsIcon"
                alt="Projects"
                class="flex-shrink-0"
                width="15"
              />
              <span class="font-medium">Projects</span>
            </router-link>
            <router-link
              to="/freelance/diary"
              class="flex items-center gap-2 p-2 text-surface-700 hover:bg-surface-100 rounded-lg transition-colors border-r-2 border-transparent"
              :class="{ '!border-amber-500 bg-amber-50 text-amber-700': route.path === '/freelance/diary' }"
            >
              <Image
                :src="DiaryIcon"
                alt="Diary"
                class="flex-shrink-0"
                width="15"
              />
              <span class="font-medium">Diary</span>
            </router-link>
            <router-link
              to="/freelance/invoices"
              class="flex items-center gap-2 p-2 text-surface-700 hover:bg-surface-100 rounded-lg transition-colors border-r-2 border-transparent"
              :class="{ '!border-amber-500 bg-amber-50 text-amber-700': route.path === '/freelance/invoices' }"
            >
              <Image
                :src="InvoicesIcon"
                alt="Invoices"
                class="flex-shrink-0"
                width="15"
              />
              <span class="font-medium">Invoices</span>
            </router-link>
            <router-link
              to="/freelance/expenses"
              class="flex items-center gap-2 p-2 text-surface-700 hover:bg-surface-100 rounded-lg transition-colors border-r-2 border-transparent"
              :class="{ '!border-amber-500 bg-amber-50 text-amber-700': route.path === '/freelance/expenses' }"
            >
              <Image
                :src="ExpensesIcon"
                alt="Expenses"
                class="flex-shrink-0"
                width="15"
              />
              <span class="font-medium">Expenses</span>
            </router-link>
          </div>
        </div>
      </div>

      <!-- Bottom buttons: same as main sidebar (Profile, Notifications, Chat, Settings, Log out) -->
      <div class="mt-auto border-t border-surface p-2 flex justify-between flex-shrink-0">
        <router-link
          v-tooltip.top="'Profile'"
          to="/freelance/profile"
          class="w-10 h-10 cursor-pointer inline-flex items-center justify-center border-2 border-surface hover:bg-surface-100 transition-colors duration-150 rounded-full no-underline text-inherit"
          aria-label="Profile"
        >
          <Image
            :src="ProfileIcon"
            alt=""
            role="presentation"
            class="px-2 pointer-events-none select-none"
            width="15"
            draggable="false"
          />
        </router-link>
        <router-link
          v-slot="{ href }"
          to="/notifications"
          custom
        >
          <a
            v-tooltip.top="'Notifications'"
            :href="href"
            class="w-10 h-10 cursor-pointer inline-flex items-center justify-center border-2 border-surface hover:bg-surface-100 transition-colors duration-150 rounded-full relative no-underline text-inherit"
            aria-label="Notifications"
            @click="onNotificationsLinkClick"
          >
            <Image
              :src="NotificationIcon"
              alt=""
              role="presentation"
              class="px-2 pointer-events-none select-none"
              width="15"
              draggable="false"
            />
            <Badge
              v-if="notificationsStore.unreadCount > 0"
              :value="notificationsStore.unreadCount > 99 ? '99+' : notificationsStore.unreadCount"
              severity="danger"
              class="absolute -top-0.5 -right-0.5 min-w-[1.25rem] h-5 flex items-center justify-center text-xs pointer-events-none"
            />
          </a>
        </router-link>
        <button
          v-tooltip.top="'Chat'"
          class="w-10 h-10 cursor-pointer inline-flex items-center justify-center border-2 border-surface hover:bg-surface-100 transition-colors duration-150 rounded-full"
          @click="openChatWindow"
        >
          <Image
            :src="ChatIcon"
            alt="Chat"
            class="px-2"
            width="15"
          />
        </button>
        <router-link
          v-tooltip.top="'Settings'"
          to="/freelance/settings"
          class="w-10 h-10 cursor-pointer inline-flex items-center justify-center border-2 border-surface hover:bg-surface-100 transition-colors duration-150 rounded-full no-underline text-inherit"
          aria-label="Settings"
        >
          <Image
            :src="SettingsIcon"
            alt=""
            role="presentation"
            class="px-2 pointer-events-none select-none"
            width="15"
            draggable="false"
          />
        </router-link>
        <button
          v-tooltip.top="'Log out'"
          class="w-10 h-10 cursor-pointer inline-flex items-center justify-center text-primary-contrast border-2 border-primary-emphasis hover:bg-primary-emphasis transition-colors duration-150 rounded-full"
          @click="showLogoutModal"
        >
          <Image
            :src="LogOutIcon"
            alt="Log out"
            class="px-2"
            width="15"
          />
        </button>
      </div>
    </div>
  </div>

  <!-- Notifications Drawer (same as main sidebar) -->
  <Drawer
    v-model:visible="notificationDrawerVisible"
    position="right"
    header="Notifications"
    class="!w-full sm:!w-[22rem]"
    :dismissable="true"
  >
    <template #footer>
      <div
        v-if="notificationsStore.unreadCount > 0"
        class="flex justify-end"
      >
        <Button
          label="Mark all as read"
          size="small"
          text
          @click="notificationsStore.markAllAsRead()"
        />
      </div>
    </template>
    <NotificationsListContent :close-drawer="closeNotificationDrawer" />
  </Drawer>

  <!-- Logout confirmation dialog -->
  <Dialog
    v-model:visible="showModal"
    header="Log out"
    modal
    :style="{ width: '22rem' }"
    :closable="true"
  >
    <p>Are you sure you want to log out?</p>
    <template #footer>
      <Button
        label="Cancel"
        severity="secondary"
        @click="cancelLogout"
      />
      <Button
        label="Log out"
        severity="danger"
        @click="confirmLogout"
      />
    </template>
  </Dialog>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import Logo from '@/assets/logos/Symphony_Logo_Only.png';
import DashboardIcon from '@/assets/icons/Menu.svg';
import DiaryIcon from '@/assets/icons/Calendar.svg';
import ProjectsIcon from '@/assets/icons/Projects.svg';
import InvoicesIcon from '@/assets/icons/Income.svg';
import ExpensesIcon from '@/assets/icons/Paperwork.svg';
import ContactsIcon from '@/assets/icons/NewClient.svg';
import ProfileIcon from '@/assets/icons/UserManagement.svg';
import NotificationIcon from '@/assets/icons/Notification.svg';
import ChatIcon from '@/assets/icons/Chat.svg';
import SettingsIcon from '@/assets/icons/Settings.svg';
import LogOutIcon from '@/assets/icons/Logout.svg';
import { useAuthStore } from '@/stores/authStore';
import { useNotificationsStore } from '@/stores/notificationsStore';
import { useChatWindow } from '@/composables/useChatWindow';
import NotificationsListContent from '@/components/notifications/NotificationsListContent.vue';

const route = useRoute();
const router = useRouter();
const authStore = useAuthStore();
const notificationsStore = useNotificationsStore();
const { openChatWindow } = useChatWindow();
const showModal = ref(false);
const notificationDrawerVisible = ref(false);

onMounted(() => {
  notificationsStore.loadNotifications();
});

const openNotificationDrawer = () => {
  notificationDrawerVisible.value = true;
  notificationsStore.loadNotifications();
};

const onNotificationsLinkClick = (e) => {
  if (e.ctrlKey || e.metaKey || e.shiftKey || e.altKey || e.button !== 0) {
    return;
  }
  e.preventDefault();
  openNotificationDrawer();
};

const closeNotificationDrawer = () => { notificationDrawerVisible.value = false; };

const showLogoutModal = () => { showModal.value = true; };
const cancelLogout = () => { showModal.value = false; };
const confirmLogout = async () => {
  try {
    await authStore.logout();
    router.push('/');
  } catch (e) {
    console.error('Logout failed', e);
  }
  showModal.value = false;
};
</script>

<style scoped>
/* Sidebar column - no extra styles needed */
</style>
